/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * scale.ts - Utility functions for scaling with screen size
 */

import { Dimensions, Platform, PixelRatio, useWindowDimensions } from 'react-native';
import { useHeaderHeight } from '@react-navigation/elements';
import { useSafeAreaFrame } from 'react-native-safe-area-context';
import { useMemo } from 'react';

const { width } = Dimensions.get('window');
// based on iphone 5s's scale
const scale = width / 320;

export function normalize(size: number) {
  const newSize = size * scale;
  if (Platform.OS === 'ios') {
    return Math.round(PixelRatio.roundToNearestPixel(newSize));
  }
  return Math.round(PixelRatio.roundToNearestPixel(newSize)) - 2;
}

/**
 * Calculates the largest rectangle of a given aspect ratio that _is contained in_ some given dimensions.
 * So, one dimension will equal the corresponding dimensions of the constrains, and the other will be smaller.
 *
 * This is similar to the `resizeMode: 'contain'` which is a style prop for `<Image>` components.
 *
 * @param constraints - the dimensions to contain the rectangle within
 * @param aspectRatio - the aspect ratio to use (width/height)
 */
export const containAspectRatio = (
  constraints: { width: number, height: number},
  aspectRatio: number
): { width: number, height: number} => {
  // Try first using parent's width and calculating a new height.
  const height = constraints.width / aspectRatio;

  if (height <= constraints.height) {
    // It is contained in the constraints
    return { width: constraints.width, height: height };
  } else {
    // Resulting rectangle bigger than parent - use parent's height instead.
    return { width: constraints.height * aspectRatio, height: constraints.height };
  }
};

/**
 * Calculates the largest rectangle of a given aspect ratio that _covers_ some given dimensions.
 * So, one dimension will equal the corresponding dimensions of the constrains, and the other will be larger.
 *
 * This is similar to the `resizeMode: 'cover'` which is a style prop for `<Image>` components.
 *
 * @param constraints - the dimensions to cover with the rectangle
 * @param aspectRatio - the aspect ratio to use (width/height)
 */
export const coverAspectRatio = (
  constraints: { width: number, height: number},
  aspectRatio: number
): { width: number, height: number} => {
  // Try first using parent's width and calculating a new height.
  const height = constraints.width / aspectRatio;

  if (height >= constraints.height) {
    // It covers the constraints
    return { width: constraints.width, height: height };
  } else {
    // Resulting rectangle smaller than parent - use parent's height instead.
    return { width: constraints.height * aspectRatio, height: constraints.height };
  }
};

/**
 * For a simple screen shown by our stack navigator, this finds the total dimensions into which the component can
 * render.
 * (Navigation screen is not to be confused with the device's screen. Device screen >= window >= nav screen.)
 *
 * This includes unsafe areas (such as notches). You may want to additionally subtract the safe area insets, e.g.
 * if you're wrapping things in a SafeAreaView.
 */
export const useNavScreenDimensions = () => {
  // For native, we use useSafeAreaFrame. This is roughly equivalent to useWindowDimensions, except the latter is
  // bugged on Android to not include the system bar even though our app extends into that system bar.
  // useSafeAreaFrame doesn't work for web, so just fall back to useWindowDimensions.
  // Hooks shouldn't be called conditionally if the condition changes, but this condition cannot change.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const {width: windowWidth, height: windowHeight} = Platform.OS === 'web' ? useWindowDimensions() : useSafeAreaFrame();
  const headerHeight = useHeaderHeight(); // Might be 0 if there is no header

  return useMemo(() => {
    // Always takes up the whole window, minus the header height.
    return {
      width: windowWidth,
      height: windowHeight - headerHeight
    };
  }, [headerHeight, windowWidth, windowHeight]);
};
